import get from 'lodash/get';
import { fetchReferences } from '../../../../services/References';
import {
  References,
  ContentGeneratedResult,
  ContentTranslatedResult,
  ReferencesResponseData,
} from '../../../../types/Content';
import store from '../../index';
import { contentClaimsStateSegment } from '../../stateSegments/contentClaimsStateSegment';
import {
  claimsSequenceStateSegment,
  ClaimsSequenceType,
} from '../../stateSegments/claimsSequenceStateSegment';
import { ContentType } from '../../../../constants/common';
import { emailContentParentId } from '../../stateSegments/emailModulesStateSegment';

export const getClaimsReferences = async (
  contentId: string,
  noOfRetries: number,
  sessionId: string,
  action: string
) => {
  store.set(contentClaimsStateSegment, prev => {
    return {
      ...prev,
      isLoading: true,
      map: {
        ...prev.map,
        [contentId]: {
          claims: [],
          retries: noOfRetries,
          errorLoadingClaims: false,
          errorMessage: '',
        },
      },
    };
  });

  const { data, success } = await fetchReferences(contentId, sessionId, action);
  if (success && data) {
    // Sort the claims: QUOTEs before CLAIMs
    const sortedClaims = [
      ...(data as ReferencesResponseData).contentReferences,
    ].sort((a, b) => {
      if (a.type === 'QUOTE' && b.type !== 'QUOTE') return -1;
      if (a.type !== 'QUOTE' && b.type === 'QUOTE') return 1;
      return 0; // Maintain original order within each type
    });

    setTimeout(() => {
      store.set(contentClaimsStateSegment, prev => {
        return {
          ...prev,
          isLoading: false,
          map: {
            ...prev.map,
            [contentId]: {
              claims: sortedClaims,
              retries: noOfRetries,
              errorLoadingClaims: false,
              errorMessage: '',
            },
          },
        };
      });
    }, 3000);
  } else {
    store.set(contentClaimsStateSegment, prev => {
      return {
        ...prev,
        isLoading: false,
        map: {
          ...prev.map,
          [contentId]: {
            errorLoadingClaims: true,
            errorMessage: 'There was an error loading claims.',
            claims: [],
            retries: noOfRetries,
          },
        },
      };
    });
  }
};

export const sequenceAndStoreClaims = (
  data: ContentGeneratedResult['data'] | ContentTranslatedResult['data'],
  isTranslatedContent: boolean = false
): ClaimsSequenceType => {
  const channelName = get(data, 'channel', '');
  const isEmailChannel =
    channelName.toLowerCase() === ContentType.EMAIL.toLowerCase();
  const isRTEChannel =
    channelName.toLowerCase() === ContentType.RTE.toLowerCase();
  const emailParentId =
    store.get(emailContentParentId) ||
    get(
      data,
      isTranslatedContent
        ? 'translatedContent[0].id'
        : 'generatedContent[0].id',
      ''
    );
  const claimsSequences = store.get(claimsSequenceStateSegment);
  const claimReferences = store.get(contentClaimsStateSegment);
  const contentId = isEmailChannel
    ? emailParentId
    : get(
        data,
        isTranslatedContent
          ? 'translatedContent[0].id'
          : 'generatedContent[0].id',
        ''
      );
  const contentClaims = get(claimReferences, `map[${contentId}].claims`, []);
  // Determine content references from payload so we can check whether
  // existing sequences are still valid (e.g. when toggling Extended Knowledge).
  const contentReferences: References[] = get(
    data,
    isTranslatedContent
      ? 'translatedContent[0].contentReferences'
      : 'generatedContent[0].contentReferences',
    []
  );

  // If there are already sequences stored, only reuse them when they match
  // the current set of content reference ids. This prevents reusing stale
  // sequences when citations change (for example after enabling CF/Extended Knowledge).
  const existingSeqKeys = Object.keys(claimsSequences || {});
  const expectedIds = (contentReferences || []).map(ref => ref.id);

  // Compare both presence and order. If either differs, we should recompute.
  const sameLength = existingSeqKeys.length === expectedIds.length;
  const sameOrder = sameLength
    ? expectedIds.every((id, idx) => existingSeqKeys[idx] === id)
    : false;

  const sequencesMatch = sameLength && sameOrder;

  if (!isRTEChannel && existingSeqKeys.length && sequencesMatch) {
    return claimsSequences;
  }

  if (contentClaims.length || isTranslatedContent) {
    return claimsSequences;
  }

  // Sort references: QUOTEs first, then CLAIMs (matching the sort in References component)
  const sortedContentReferences = [...contentReferences].sort((a, b) => {
    if (a.type === 'QUOTE' && b.type !== 'QUOTE') return -1;
    if (a.type !== 'QUOTE' && b.type === 'QUOTE') return 1;
    return 0; // Maintain original order within each type
  });

  let previewClaims = [...sortedContentReferences];
  if (isRTEChannel) {
    const parentClaims = get(
      claimReferences,
      `map[${emailParentId}].claims`,
      []
    );
    const ids: string[] = [];
    const mergedClaims = [...parentClaims, ...sortedContentReferences].filter(
      (ref: References) => {
        if (!ids.includes(ref.id)) {
          ids.push(ref.id);
          return true;
        }
        return false;
      }
    );
    // Sort the merged preview claims as well
    previewClaims = mergedClaims.sort((a, b) => {
      if (a.type === 'QUOTE' && b.type !== 'QUOTE') return -1;
      if (a.type !== 'QUOTE' && b.type === 'QUOTE') return 1;
      return 0;
    });
  }

  // Create sequences based on sorted order
  // For RTE flow, use previewClaims order; otherwise use sortedContentReferences
  const referencesForSequencing = isRTEChannel
    ? previewClaims
    : sortedContentReferences;
  const seqObj: ClaimsSequenceType = {};
  referencesForSequencing.forEach((ref: References, index: number) => {
    seqObj[ref.id] = `${index + 1}`;
  });
  store.set(contentClaimsStateSegment, {
    isLoading: false,
    map: {
      [contentId]: {
        claims: sortedContentReferences,
        retries: 3,
        errorLoadingClaims: false,
        errorMessage: '',
      },
    },
    previewClaims,
  });
  store.set(claimsSequenceStateSegment, seqObj);
  return seqObj;
};
